import * as Apollo from "@apollo/client";

import { flow, pipe, TaskEither } from "fp";

import * as Data from "~/Data";
import * as ELD from "~/ELD";
import { Time } from "~/Time";
import { WebAPI } from "~/WebAPI";

export namespace Get {
  type Options = Time.Interval;

  /**
   * Every submission in the window, for every driver in the viewer's company.
   *
   * Company scope is the caller's own — `companyEldSubmissions` reads it from the
   * token rather than taking it as an argument — so unlike the REST endpoint this
   * replaces, there is no company id to pass.
   */
  export const request = (options: Options): Data.Cache.Request<ELD.ELD> => (
    context
  ) =>
    TaskEither.tryCatch(
      () => fromClient(WebAPI.clientFor(context), options),
      WebAPI.ServiceError.toError
    );

  /**
   * The query is a forward-only connection, but the page it feeds sorts,
   * searches and exports across the whole set, so a partial list would quietly
   * give wrong answers to all three. Follow the cursor to the end and hand back
   * one cache.
   *
   * Bounded by the six-month window rather than by a page count: that is the
   * retention period, so the window is what keeps this from growing without
   * limit.
   *
   * Takes the client rather than reaching for it so the paging can be tested
   * against a stub without standing up a `Context`.
   */
  export const fromClient = async (
    client: Apollo.ApolloClient<unknown>,
    options: Options
  ): Promise<Data.Cache.Cache<ELD.ELD>> => {
    // eslint-disable-next-line functional/prefer-readonly-type
    const submissions: WebAPI.ELD.SubmissionFragment[] = [];

    // eslint-disable-next-line functional/no-let
    let after: string | undefined = undefined;

    // eslint-disable-next-line functional/no-loop-statement
    do {
      const connection: Connection = await page(client, options, after);

      // eslint-disable-next-line functional/no-expression-statement,functional/immutable-data
      submissions.push(
        ...(connection?.edges ?? []).flatMap((edge) =>
          edge?.node ? [edge.node] : []
        )
      );

      // A page that claims another page but hands back no cursor to reach it
      // would re-request this same page forever. Stop instead.
      after = connection?.pageInfo.hasNextPage
        ? connection.pageInfo.endCursor ?? undefined
        : undefined;
    } while (after);

    return toCache(submissions);
  };

  type Connection = WebAPI.ELD.CompanyEldSubmissionsResult["companyEldSubmissions"];

  const page = async (
    client: Apollo.ApolloClient<unknown>,
    options: Options,
    after: string | undefined
  ): Promise<Connection> => {
    const { data } = await client.query<
      WebAPI.ELD.CompanyEldSubmissionsResult,
      WebAPI.ELD.CompanyEldSubmissionsVariables
    >({
      query: WebAPI.ELD.CompanyEldSubmissionsDocument,
      variables: {
        // The range control brackets whole days in the viewer's own zone, so
        // the day is read back in that zone too. Via UTC, a viewer east of it
        // would ask for a window starting the day before the one they picked,
        // and one west of it for a day past the end.
        startDate: Time.DateTime.toISOLocalWithoutTime(options.interval.start),
        endDate: Time.Interval.isStopped(options)
          ? Time.DateTime.toISOLocalWithoutTime(options.interval.stop)
          : undefined,
        first: PAGE_SIZE,
        after,
      },
      // The page is a snapshot of a moving list, and a resubmit refetches it
      // expecting to see the row it just created.
      fetchPolicy: "network-only",
    });

    return data.companyEldSubmissions;
  };

  /**
   * The API's own cap. Asking for more is silently clamped to it, so requesting
   * the cap exactly is the fewest round trips available.
   */
  const PAGE_SIZE = 100;

  // Built per call rather than once at module scope: `~/ELD` and this module
  // import each other, so `Data.Cache` is still undefined while this module's
  // body runs.
  const toCache = (
    submissions: readonly WebAPI.ELD.SubmissionFragment[]
  ): Data.Cache.Cache<ELD.ELD> =>
    pipe(
      submissions,
      Data.Cache.fromList(
        flow(ELD.fromWebAPI, (eld): readonly [string, ELD.ELD] => [eld.id, eld])
      )
    );
}
